import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { isValidSiret, normalizeSiret, SIRET_ERROR } from '../utils/siret';

export default function ServiceUNAF() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignYearParam = searchParams.get('annee');
  const signatureRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [canSubscribe, setCanSubscribe] = useState(false);
  const [adhesion, setAdhesion] = useState(null);
  const [existingService, setExistingService] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [step, setStep] = useState(1); // 1: Infos, 2: Options, 3: Récapitulatif & Signature
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [sendingContact, setSendingContact] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [serviceDisabled, setServiceDisabled] = useState(false);
  const [anneeCampagne, setAnneeCampagne] = useState(null);
  const [nextUnafExportDate, setNextUnafExportDate] = useState(null);
  
  const [formData, setFormData] = useState({
    siret: '',
    napi: '',
    nombreEmplacements: 0,
    nombreRuches: 0,
    // Options
    affairesJuridiques: false,
    ecocontribution: false,
    revue: 'aucun',
    assuranceFormule: 'aucune',
    // Validation
    acceptConditionsGroupama: false,
    acceptConditionsAdelphe: false,
    acceptConditions: false,
    signature: '',
  });

  // Tarifs
  const TARIFS = {
    cotisationUNAF: 1.50,
    affairesJuridiques: 0.15,
    ecocontribution: 0.12,
    revue: {
      papier: 31,
      numerique: 18,
      papier_numerique: 35,
      aucun: 0,
    },
    assurance: {
      aucune: 0,
      formule1: 0.10,
      formule2: 1.65,
      formule3: 2.80,
    },
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    checkServiceEnabled();
    checkEligibility();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, campaignYearParam]);

  const checkServiceEnabled = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/service-settings`);
      if (!response.data.unafActif) {
        setServiceDisabled(true);
        setError('Ce service est actuellement indisponible.');
      }
    } catch (err) {
      console.error('Erreur vérification service:', err);
    }
  };

  const fetchNextUnafExportDate = async (annee) => {
    try {
      const year = annee || new Date().getFullYear();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/public/next-unaf-export?annee=${year}`);
      setNextUnafExportDate(response.data.nextExportDateFormatted);
    } catch (err) {
      console.error('Erreur lors du chargement de la prochaine date d\'export UNAF:', err);
    }
  };

  const checkEligibility = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Récupérer les données utilisateur fraîches pour avoir napi et siret à jour
      const userResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const userData = userResponse.data;

      const query = campaignYearParam ? `?annee=${campaignYearParam}` : '';
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/can-subscribe/assurance-unaf${query}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCanSubscribe(response.data.canSubscribe);
      setAdhesion(response.data.adhesion);
      setExistingService(response.data.existingService);
      setPrefillData(response.data.prefillData);
      setAnneeCampagne(response.data.anneeCampagne);

      const exportYear = response.data.anneeCampagne || campaignYearParam || new Date().getFullYear();
      fetchNextUnafExportDate(exportYear);
      
      if (response.data.prefillData) {
        setFormData(prev => ({
          ...prev,
          siret: response.data.prefillData.siret || userData?.siret || '',
          napi: response.data.prefillData.napi || userData?.napi || '',
          nombreEmplacements: response.data.prefillData.nombreEmplacements || 0,
          nombreRuches: response.data.prefillData.nombreRuches || 0,
          // Pré-remplir les options existantes
          assuranceFormule: response.data.prefillData.assuranceFormule || 'aucune',
          revue: response.data.prefillData.revue || 'aucun',
          affairesJuridiques: response.data.prefillData.affairesJuridiques || false,
          ecocontribution: response.data.prefillData.ecocontribution || false,
        }));
      } else {
        // Si pas de prefillData, utiliser les données du profil utilisateur
        setFormData(prev => ({
          ...prev,
          siret: userData?.siret || '',
          napi: userData?.napi || '',
        }));
      }
      
      if (!response.data.canSubscribe) {
        setError(response.data.reason);
      } else {
        setError('');
      }
    } catch (err) {
      setError('Erreur lors de la vérification de l\'éligibilité');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    const nombreRuches = formData.nombreRuches || 0;
    
    let total = TARIFS.cotisationUNAF;
    
    if (formData.affairesJuridiques) {
      total += TARIFS.affairesJuridiques * nombreRuches;
    }
    
    if (formData.ecocontribution) {
      total += TARIFS.ecocontribution * nombreRuches;
    }
    
    total += TARIFS.revue[formData.revue] || 0;
    total += (TARIFS.assurance[formData.assuranceFormule] || 0) * nombreRuches;
    
    return Math.round(total * 100) / 100;
  };

  const getDetailMontants = () => {
    const nombreRuches = formData.nombreRuches || 0;
    return {
      cotisationUNAF: TARIFS.cotisationUNAF,
      affairesJuridiques: formData.affairesJuridiques ? TARIFS.affairesJuridiques * nombreRuches : 0,
      ecocontribution: formData.ecocontribution ? TARIFS.ecocontribution * nombreRuches : 0,
      revue: TARIFS.revue[formData.revue] || 0,
      assurance: (TARIFS.assurance[formData.assuranceFormule] || 0) * nombreRuches,
    };
  };

  const hasSelectedOptions = () => {
    // Vérifier si au moins une option est sélectionnée
    return (
      formData.assuranceFormule !== 'aucune' ||
      formData.revue !== 'aucun' ||
      formData.affairesJuridiques ||
      formData.ecocontribution
    );
  };

  const handleSignature = () => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.toDataURL();
      setFormData(prev => ({ ...prev, signature: signatureData }));
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setFormData(prev => ({ ...prev, signature: '' }));
    }
  };

  const handleSubmit = async () => {
    // Validation NAPI si nombre de ruches > 0
    if (formData.nombreRuches > 0 && !formData.napi?.trim()) {
      setError('Le NAPI est obligatoire si vous avez des ruches');
      return;
    }

    if (!isValidSiret(formData.siret, {
      required: Boolean(formData.ecocontribution) || (formData.assuranceFormule && formData.assuranceFormule !== 'aucune'),
    })) {
      setError(formData.siret?.trim() ? SIRET_ERROR : 'Le SIRET (14 chiffres) est requis pour cette option');
      return;
    }

    if (!formData.acceptConditions) {
      setError('Veuillez accepter les conditions');
      return;
    }

    if (!formData.signature) {
      setError('Veuillez signer le formulaire');
      return;
    }

    // Validation Groupama uniquement si formule assurance sélectionnée
    if (formData.assuranceFormule && formData.assuranceFormule !== 'aucune' && !formData.acceptConditionsGroupama) {
      setError('Veuillez accepter les conditions générales des assurances Groupama');
      return;
    }

    // Validation Adelphe si écocontribution cochée
    if (formData.ecocontribution && !formData.acceptConditionsAdelphe) {
      setError('Veuillez accepter les Conditions Générales d\'Adelphe pour la REP Emballages Ménagers et Papiers Graphiques (éco-contribution)');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const token = localStorage.getItem('token');

      // Créer la souscription au service
      const createResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/services`,
        {
          typeService: 'assurance_unaf',
          adhesionId: adhesion._id,
          typePaiement: 'en_ligne',
          signature: formData.signature,
          acceptationReglement: formData.acceptConditions,
          unafOptions: {
            // Toujours envoyer le SIRET pour l'enregistrer dans la fiche service
            siret: formData.siret,
            napi: formData.napi,
            nombreEmplacements: formData.nombreEmplacements,
            nombreRuches: formData.nombreRuches,
            affairesJuridiques: formData.affairesJuridiques,
            ecocontribution: formData.ecocontribution,
            revue: formData.revue,
            assuranceFormule: formData.assuranceFormule,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const service = createResponse.data;

      // Créer une session de paiement Stripe
      const paymentResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/payment/service/create-payment-session/${service._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Rediriger vers Stripe
      window.location.href = paymentResponse.data.url;
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la souscription');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(montant);
  };

  const handleSendContact = async (e) => {
    e.preventDefault();
    setSendingContact(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/adhesions/${adhesion._id}/demande-aide`,
        { message: contactMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setContactSuccess(true);
      setTimeout(() => {
        setShowContactModal(false);
        setContactSuccess(false);
        setContactMessage('');
      }, 2000);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setSendingContact(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Si service désactivé
  if (serviceDisabled) {
    return (
      <div className="bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Service temporairement indisponible
            </h1>
            <p className="text-gray-600 mb-6">
              Les inscriptions aux services de l'UNAF sont actuellement fermées.
              Veuillez réessayer ultérieurement.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Retour au tableau de bord
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Si déjà souscrit
  if (existingService) {
    return (
      <div className="bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Vous êtes déjà inscrit aux services de l'UNAF
            </h1>
            <p className="text-gray-600 mb-6">
              Votre souscription pour l'année {existingService.annee} est{' '}
              {existingService.status === 'actif' ? 'active' : 'en cours de traitement'}.
            </p>
            <button
              onClick={() => navigate('/mes-adhesions')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Retour à mes adhésions
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Si non éligible
  if (!canSubscribe) {
    return (
      <div className="bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Non éligible aux services de l'UNAF
            </h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-x-4">
              <button
                onClick={() => navigate('/adhesion/sar')}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                Adhérer au SAR
              </button>
              <button
                onClick={() => navigate('/mes-adhesions')}
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Retour
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Modal Contact */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Besoin d'aide ?
              </h3>
              <button
                onClick={() => setShowContactModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {contactSuccess ? (
              <div className="text-center py-8">
                <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <p className="text-green-600 font-medium">
                  Message envoyé avec succès !
                </p>
              </div>
            ) : (
              <form onSubmit={handleSendContact}>
                <p className="text-gray-600 mb-4">
                  Décrivez votre problème ou votre question concernant cette adhésion. 
                  Un administrateur du groupement SAR vous répondra par email.
                </p>
                
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  required
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Décrivez votre demande..."
                />

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowContactModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={sendingContact}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400"
                  >
                    {sendingContact ? 'Envoi...' : 'Envoyer'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <div className="bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* En-tête */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Services de l'UNAF
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            Souscription pour l'année {anneeCampagne || adhesion?.annee}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Service proposé par le Syndicat Apicole de La Réunion (SAR), adhérent de l'UNAF
          </p>
          {nextUnafExportDate && (
            <div className="mt-4 inline-block bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <p className="text-sm text-blue-900">
                <span className="font-medium">📅 La prochaine transmission des soucriptions à l'UNAF est prévue en date du</span> {nextUnafExportDate}
              </p>
            </div>
          )}
        </div>

        {/* Indicateur d'étapes */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div
                    className={`w-12 h-1 mx-2 ${
                      step > s ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-2 space-x-8 text-sm text-gray-600">
            <span>Informations</span>
            <span>Options</span>
            <span>Récapitulatif</span>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Étape 1: Informations */}
        {step === 1 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Informations apicoles</h2>
            {/* siret placeholder user data */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SIRET (si professionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.siret}
                    maxLength="14"
                    onChange={(e) => {
                      // Ne garder que les chiffres
                      const newSiret = normalizeSiret(e.target.value);
                      const hasNapi = formData.napi?.trim();
                      const shouldReset = !newSiret.trim() || !hasNapi;
                      
                      // Alertes si des options sont décochées
                      if (shouldReset && formData.ecocontribution) {
                        alert('⚠️ L\'écocontribution a été automatiquement décochée car le SIRET et le NAPI sont requis pour cette option.');
                      }
                      if (shouldReset && formData.assuranceFormule !== 'aucune') {
                        alert('⚠️ La formule d\'assurance a été automatiquement désélectionnée car le SIRET et le NAPI sont requis pour cette option.');
                      }
                      
                      setFormData(prev => ({
                        ...prev,
                        siret: newSiret,
                        // Décocher écocontribution et réinitialiser formule assurance si SIRET ou NAPI est vidé
                        ecocontribution: (newSiret.trim() && hasNapi) ? prev.ecocontribution : false,
                        assuranceFormule: (newSiret.trim() && hasNapi) ? prev.assuranceFormule : 'aucune'
                      }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="14 chiffres exactement (ex: 12345678901234)"
                  />
                  {formData.siret && formData.siret.length !== 14 && (
                    <p className="text-xs text-red-500 mt-1">{SIRET_ERROR}</p>
                  )}
                </div>

            {/* napi placeholder user data */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    NAPI {formData.nombreRuches > 0 && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.napi}
                    onChange={(e) => {
                      const newNapi = e.target.value;
                      const hasSiret = formData.siret?.trim();
                      const shouldReset = !hasSiret || !newNapi.trim();
                      
                      // Alertes si des options sont décochées
                      if (shouldReset && formData.ecocontribution) {
                        alert('⚠️ L\'écocontribution a été automatiquement décochée car le SIRET et le NAPI sont requis pour cette option.');
                      }
                      if (shouldReset && formData.assuranceFormule !== 'aucune') {
                        alert('⚠️ La formule d\'assurance a été automatiquement désélectionnée car le NAPI est requis pour cette option.');
                      }
                      
                      setFormData(prev => ({
                        ...prev,
                        napi: newNapi,
                        // Décocher écocontribution et réinitialiser formule assurance si SIRET ou NAPI est vidé
                        ecocontribution: (hasSiret && newNapi.trim()) ? prev.ecocontribution : false,
                        assuranceFormule: (hasSiret && newNapi.trim()) ? prev.assuranceFormule : 'aucune'
                      }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Numéro NAPI"
                  />
                  {formData.nombreRuches > 0 && !formData.napi?.trim() && (
                    <p className="text-xs text-red-500 mt-1">
                      Le NAPI est obligatoire si vous avez des ruches
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre d'emplacements (ruchers) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.nombreEmplacements}
                    onChange={(e) => setFormData({ ...formData, nombreEmplacements: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de ruches *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.nombreRuches}
                    onChange={(e) => setFormData({ ...formData, nombreRuches: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ce nombre servira au calcul des cotisations
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={() => {
                  if (formData.nombreRuches > 0 && !formData.napi?.trim()) {
                    setError('Le NAPI est obligatoire si vous avez des ruches');
                    return;
                  }
                  if (!isValidSiret(formData.siret, { required: false })) {
                    setError(SIRET_ERROR);
                    return;
                  }
                  setError('');
                  setStep(2);
                }}
                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Continuer
              </button>
            </div>
          </div>
        )}

        {/* Étape 2: Options */}
        {step === 2 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Choix des options</h2>
            
            <div className="space-y-6">
              {/* Cotisation obligatoire */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Cotisation  <span className="text-sm text-gray-500 font-normal">(obligatoire)</span></h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Cotisation UNAF</span>
                    <span className="font-medium">{formatMontant(TARIFS.cotisationUNAF)}</span>
                  </div>
                </div>
              </div>

              {/* Avertissement nombre de ruches */}
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="ml-3 text-sm text-blue-700">
                    <strong>Attention :</strong> Le nombre de ruches pris en compte est le même pour tous les services souscrits.
                  </p>
                </div>
              </div>

              {/* Assurance (optionnelle) */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Assurance  <span className="text-sm text-gray-500 font-normal">(facultative)</span></h3>
                  <a
                    href="https://www.unaf-apiculture.info/nos-services/assurances-et-declaration-de-sinistres/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center"
                  >
                    Détails des formules sur le site de l'UNAF
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                  </a>
                </div>
                <div className="space-y-3">

                  {/* Avertissement important sur la continuité d'assurance */}
                  <div className="bg-honey-50 border-l-4 border-honey-400 p-4 rounded-r-lg">
                    <div className="ml-2">
                      <h3 className="text-sm font-bold text-honey-800 mb-2">
                        ⚠️ Important - Continuité d'assurance
                      </h3>
                      <p className="text-sm text-honey-700">
                        <strong>Attention :</strong> La continuité d'assurance des ruches au 1er janvier pour les adhérents assurés l'année précédente n'est possible que si le paiement intervient <strong>au plus tard le 18 janvier</strong>.
                      </p>
                      <p className="text-sm text-honey-700 mt-2">
                        Au-delà de cette date, la période d'assurance ne commencera qu'à la date de réception du paiement par l'UNAF.
                      </p>
                    </div>
                  </div>

                  {/* Option par défaut - Aucune formule */}
                  <label className="flex items-start p-3 bg-white rounded-md border cursor-pointer hover:border-indigo-400">
                    <input
                      type="radio"
                      name="assurance"
                      value="aucune"
                      checked={formData.assuranceFormule === 'aucune'}
                      onChange={(e) => setFormData({ ...formData, assuranceFormule: e.target.value })}
                      className="mt-1 h-4 w-4 text-indigo-600"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-900">Aucune formule</span>
                        <span className="text-gray-500 font-medium">0,00 €</span>
                      </div>
                      <p className="text-sm text-gray-500">Je ne souhaite pas souscrire à une assurance</p>
                    </div>
                  </label>

                  {/* Avertissement modification de formule */}
                  <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg">
                    <p className="text-sm text-red-700">
                      <strong>Attention :</strong> Aucune modification de formule n'est acceptée en cours d'année.
                    </p>
                  </div>

                  {/* Formules avec NAPI requis */}
                  {[
                    { value: 'formule1', label: 'Formule 1', prix: 0.10, desc: 'Tarif préférentiel réservé aux adhérents du syndicat' },
                    { value: 'formule2', label: 'Formule 2', prix: 1.65, desc: 'Tarif préférentiel réservé aux adhérents du syndicat' },
                    { value: 'formule3', label: 'Formule 3', prix: 2.80, desc: 'Tarif préférentiel réservé aux adhérents du syndicat' },
                  ].map((formule) => {
                    // Vérifier si une formule a déjà été sélectionnée (modification interdite)
                    const hasExistingFormule = prefillData?.assuranceFormule && prefillData.assuranceFormule !== 'aucune';
                    const isDisabled = !formData.napi?.trim() || hasExistingFormule;
                    
                    return (
                    <label 
                      key={formule.value} 
                      className={`flex items-start p-3 bg-white rounded-md border ${!isDisabled ? 'cursor-pointer hover:border-indigo-400' : 'cursor-not-allowed opacity-60'}`}
                    >
                      <input
                        type="radio"
                        name="assurance"
                        value={formule.value}
                        checked={formData.assuranceFormule === formule.value}
                        onChange={(e) => setFormData({ ...formData, assuranceFormule: e.target.value })}
                        disabled={isDisabled}
                        className="mt-1 h-4 w-4 text-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-900">{formule.label}</span>
                          <span className="text-indigo-600 font-medium">
                            {formatMontant(formule.prix)}/ruche
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{formule.desc}</p>
                        {formData.napi?.trim() ? (
                          <p className="text-xs text-gray-400 mt-1">
                            Total: {formatMontant(formule.prix * formData.nombreRuches)} ({formData.nombreRuches} ruches)
                          </p>
                        ) : hasExistingFormule ? (
                          <p className="text-sm text-red-600 mt-1">
                            ⚠️ Modification de formule non autorisée en cours d'année
                          </p>
                        ) : (
                          <p className="text-sm text-honey-600 mt-1">
                            ⚠️{' '}
                            <button
                              type="button"
                              onClick={() => setStep(1)}
                              className="underline hover:text-honey-700 font-medium"
                            >
                              Compléter votre NAPI pour activer cette option →
                            </button>
                          </p>
                        )}
                      </div>
                    </label>
                    );
                  })}
                </div>
              </div>

              {/* Cotisation Affaires Juridiques (facultative) */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Cotisation Affaires Juridiques <span className="text-sm text-gray-500 font-normal">(facultative)</span></h3>
                </div>
                {/* Avertissement modification de formule */}
                <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg mb-4">
                  <p className="text-sm text-red-700">
                    <strong>Attention :</strong> Aucune souscription ne peut être annulée, la souscription peut intervenir en cours d'année.
                  </p>
                </div>
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.affairesJuridiques}
                    onChange={(e) => setFormData({ ...formData, affairesJuridiques: e.target.checked })}
                    className="mt-1 h-4 w-4 text-indigo-600 rounded"
                  />
                  <div className="ml-3">
                    <div className="flex items-center">
                      <span className="font-medium text-gray-900">Souscrire à la cotisation Affaires Juridiques</span>
                      <span className="ml-2 text-indigo-600">{formatMontant(TARIFS.affairesJuridiques)}/ruche</span>
                    </div>
                    <p className="text-sm text-gray-500">
                      Total: {formatMontant(TARIFS.affairesJuridiques * formData.nombreRuches)}
                    </p>
                  </div>
                </label>
              </div>

              {/* Écocontribution (facultative) */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Écocontribution <span className="text-sm text-gray-500 font-normal">(facultative)</span></h3>
                  <a
                    href="https://www.unaf-apiculture.info/nos-services/informations-reglementaires/etiquetage-ecocontribution/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center"
                  >
                    Détails sur le site de l'UNAF
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                  </a>
                </div>
                {/* Avertissement modification de formule */}
                <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg mb-4">
                  <p className="text-sm text-red-700">
                    <strong>Attention :</strong> Aucune souscription ne peut être annulée, la souscription peut intervenir en cours d'année.
                  </p>
                </div>
                <label className={`flex items-start ${(formData.siret?.trim() && formData.napi?.trim()) ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                  <input
                    type="checkbox"
                    checked={formData.ecocontribution}
                    onChange={(e) => setFormData({ ...formData, ecocontribution: e.target.checked })}
                    disabled={!(formData.siret?.trim() && formData.napi?.trim())}
                    className="mt-1 h-4 w-4 text-indigo-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <div className="ml-3">
                    <div className="flex items-center">
                      <span className="font-medium text-gray-900">Souscrire à l'écocontribution</span>
                      <span className="ml-2 text-indigo-600">{formatMontant(TARIFS.ecocontribution)}/ruche</span>
                    </div>
                    {(formData.siret?.trim() && formData.napi?.trim()) ? (
                      <p className="text-sm text-gray-500">
                        Total: {formatMontant(TARIFS.ecocontribution * formData.nombreRuches)}
                      </p>
                    ) : (
                      <p className="text-sm text-honey-600">
                        ⚠️{' '}
                        <button
                          type="button"
                          onClick={() => setStep(1)}
                          className="underline hover:text-honey-700 font-medium"
                        >
                          Compléter votre SIRET et NAPI pour activer cette option →
                        </button>
                      </p>
                    )}
                  </div>
                </label>
              </div>

              {/* Revue */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Revue "Abeilles & Fleurs"</h3>
                  <a
                    href="https://www.unaf-apiculture.info/nos-services/revue-abeilles-fleurs/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center"
                  >
                    En savoir plus sur le site de l'UNAF
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                  </a>
                </div>

                {/* Avertissement modification de formule */}
                <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg mb-4">
                  <h3 className="font-semibold text-sm text-red-700 mb-2">Règles importantes</h3>
                  <ul className="space-y-1 text-sm text-red-700">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Aucune souscription ne peut être annulée.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>La souscription peut intervenir en cours d'année.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Aucun changement de formule n'est possible en cours d'année.</span>
                    </li>
                  </ul>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'aucun', label: 'Aucune', prix: 0 },
                    { value: 'numerique', label: 'Numérique', prix: 18 },
                    { value: 'papier', label: 'Papier', prix: 31 },
                    { value: 'papier_numerique', label: 'Papier + Numérique', prix: 35 },
                  ].map((option) => {
                    // Vérifier si une revue a déjà été sélectionnée (modification interdite)
                    const hasExistingRevue = prefillData?.revue && prefillData.revue !== 'aucun';
                    const isDisabled = hasExistingRevue && option.value !== 'aucun' && option.value !== formData.revue;
                    
                    return (
                    <label
                      key={option.value}
                      className={`p-3 border rounded-md text-center transition-colors ${
                        formData.revue === option.value
                          ? 'border-indigo-500 bg-indigo-50'
                          : isDisabled
                          ? 'opacity-60 cursor-not-allowed'
                          : 'cursor-pointer hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="revue"
                        value={option.value}
                        checked={formData.revue === option.value}
                        onChange={(e) => setFormData({ ...formData, revue: e.target.value })}
                        disabled={isDisabled}
                        className="sr-only"
                        style={{left: '-100000px', right: '-100000px'}}
                      />
                      <div className="font-medium text-gray-900">{option.label}</div>
                      <div className="text-sm text-indigo-600">{formatMontant(option.prix)}</div>
                      {isDisabled && (
                        <div className="text-xs text-red-600 mt-1">Non modifiable</div>
                      )}
                    </label>
                    );
                  })}
                </div>
              </div>

              {/* Total estimé */}
              <div className="bg-indigo-600 text-white p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium">Total estimé</span>
                  <span className="text-2xl font-bold">{formatMontant(calculateTotal())}</span>
                </div>
              </div>
            </div>

            {/* Avertissement augmentation du nombre de ruches */}
            <div className="mt-6 bg-honey-50 border-l-4 border-honey-400 p-4 rounded-r-lg">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-honey-400 flex-shrink-0 mt-0.5" />
                <p className="ml-3 text-sm text-honey-700">
                  <strong>Augmentation du nombre de ruches :</strong><br/>
                  Une augmentation du nombre de ruches en cours d'année peut être prise en compte. Cette demande s'applique alors à tous les services, générant plusieurs compléments de paiement, y compris pour le montant de la cotisation de l'adhésion au SAR.
                </p>
              </div>
            </div>

            {/* Avertissement augmentation du nombre de ruches */}
            <div className="mt-6 bg-honey-50 border-l-4 border-honey-400 p-4 rounded-r-lg">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-honey-400 flex-shrink-0 mt-0.5" />
                <p className="ml-3 text-sm text-honey-700">
                  <strong>Montant minimum :</strong><br/>
                  Attention, vous ne pourrez pas souscrire à des services supplémentaires en dessous d'un montant total cumulé de 0,50€ (le montant minimum de perception permis par la plateforme).
                </p>
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Retour
              </button>
              <div className="flex flex-col items-end">
                {!hasSelectedOptions() && (
                  <p className="text-sm text-red-600 mb-2">Merci de sélectionner au moins une option</p>
                )}
                <button
                  onClick={() => {
                    if (!hasSelectedOptions()) {
                      setError('Merci de sélectionner au moins une option (formule d\'assurance, revue, affaires juridiques ou écocontribution)');
                      return;
                    }
                    
                    const total = calculateTotal();
                    if (total < 0.50) {
                      setError('Attention, vous ne pouvez pas souscrire à des services supplémentaires en dessous d\'un montant total cumulé de 0,50€ (le montant minimum de perception permis par la plateforme).');
                      return;
                    }
                    
                    setError('');
                    setStep(3);
                  }}
                  disabled={!hasSelectedOptions()}
                  className={`px-6 py-2 rounded-md ${
                    hasSelectedOptions()
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Continuer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Étape 3: Récapitulatif & Signature */}
        {step === 3 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Récapitulatif & Signature</h2>
            
            {/* Avertissement */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  Il est de votre responsabilité de vérifier le montant calculé. 
                  En cas d'erreur, veuillez{' '}
                  <button
                    type="button"
                    onClick={() => setShowContactModal(true)}
                    className="font-medium underline hover:text-yellow-900"
                  >
                    contacter l'administrateur
                  </button>
                  {' '}avant de valider.
                </p>
              </div>
            </div>

            {/* Détail */}
            <div className="border rounded-lg overflow-hidden mb-6">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h3 className="font-medium text-gray-900">Détail de votre souscription</h3>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Nombre de ruches</span>
                    <p className="font-medium">{formData.nombreRuches}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Nombre d'emplacements</span>
                    <p className="font-medium">{formData.nombreEmplacements}</p>
                  </div>
                </div>
                
                <hr />
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Cotisation UNAF</span>
                    <span>{formatMontant(getDetailMontants().cotisationUNAF)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Assurance ({formData.assuranceFormule.replace('formule', 'Formule ')})</span>
                    <span>{formatMontant(getDetailMontants().assurance)}</span>
                  </div>
                  {formData.affairesJuridiques && (
                    <div className="flex justify-between">
                      <span>Affaires juridiques</span>
                      <span>{formatMontant(getDetailMontants().affairesJuridiques)}</span>
                    </div>
                  )}
                  {formData.ecocontribution && (
                    <div className="flex justify-between">
                      <span>Écocontribution</span>
                      <span>{formatMontant(getDetailMontants().ecocontribution)}</span>
                    </div>
                  )}
                  {formData.revue !== 'aucun' && (
                    <div className="flex justify-between">
                      <span>Revue ({formData.revue === 'papier_numerique' ? 'Papier + Numérique' : formData.revue})</span>
                      <span>{formatMontant(getDetailMontants().revue)}</span>
                    </div>
                  )}
                </div>
                
                <hr />
                
                <div className="flex justify-between text-lg font-bold">
                  <span>Total à payer</span>
                  <span className="text-indigo-600">{formatMontant(calculateTotal())}</span>
                </div>
              </div>
            </div>

            {/* Signature */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Signature *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-2">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-40 bg-white rounded',
                  }}
                  onEnd={handleSignature}
                />
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={clearSignature}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Effacer la signature
                </button>
              </div>
            </div>

            {/* Acceptation conditions Groupama - uniquement si formule assurance sélectionnée */}
            {formData.assuranceFormule && formData.assuranceFormule !== 'aucune' && (
              <div className="mb-4">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.acceptConditionsGroupama}
                    onChange={(e) => setFormData({ ...formData, acceptConditionsGroupama: e.target.checked })}
                    className="mt-1 h-4 w-4 text-indigo-600 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    J'ai pris connaissance des{' '}
                    <a
                      href="https://acrobat.adobe.com/id/urn:aaid:sc:eu:68ff1de7-e2ca-4a0c-ac30-a666f526c499"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 underline"
                    >
                      Conditions générales des Assurances des activités apicoles – Groupama
                    </a>{' '}
                    et du{' '}
                    <a
                      href="https://acrobat.adobe.com/id/urn:aaid:sc:EU:99a29511-e0a0-41e7-9c8c-2eaccc7dca89?viewer%21megaVerb=group-discover"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 underline"
                    >
                      Tableau des montants de Garantie et de Franchises – Groupama
                    </a>
                    <span className="text-red-600"> *</span>
                  </span>
                </label>
              </div>
            )}

            {/* Acceptation conditions Adelphe - uniquement si écocontribution cochée */}
            {formData.ecocontribution && (
              <div className="mb-4">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.acceptConditionsAdelphe}
                    onChange={(e) => setFormData({ ...formData, acceptConditionsAdelphe: e.target.checked })}
                    className="mt-1 h-4 w-4 text-indigo-600 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    J'ai pris connaissance des{' '}
                    <a
                      href="https://www.unaf-apiculture.info/wp-content/uploads/2023/01/vdef_adelphe_conditions_generales_empg_2024.docx-261.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 underline"
                    >
                      conditions générales Adelphe (écocontribution)
                    </a>
                    <span className="text-red-600"> *</span>
                  </span>
                </label>
              </div>
            )}

            {/* Acceptation conditions UNAF */}
            <div className="mb-6">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.acceptConditions}
                  onChange={(e) => setFormData({ ...formData, acceptConditions: e.target.checked })}
                  className="mt-1 h-4 w-4 text-indigo-600 rounded"
                />
                <span className="ml-3 text-sm text-gray-700">
                  J'accepte les conditions de souscription aux services de l'UNAF et certifie l'exactitude des informations fournies.
                  <span className="text-red-600"> *</span>
                </span>
              </label>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Retour
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !formData.acceptConditions || (formData.assuranceFormule && formData.assuranceFormule !== 'aucune' && !formData.acceptConditionsGroupama) || !formData.signature}
                className="px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Traitement...' : `Payer ${formatMontant(calculateTotal())}`}
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
}
